import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { CargaSalida } from '@/types'
import { Loader } from '@/components/common/Loader'
import { enRiesgo, pickingPct, retrasoDe } from '@/utils/cargasSalida'
import { ChipFecha, ChipPalets, ChipPicking } from '@/components/muelles/ChipCarga'

type Orden = 'urgencia' | 'picking' | 'palets'

/**
 * Las cargas que aún no tienen camión.
 *
 * Es la cola de trabajo del cuadro, así que cada fila tiene que responder a tres
 * preguntas de un vistazo —cuándo sale, cuánto se ha preparado y cuánto ocupa—
 * y por eso van en chips y no en una línea de texto gris: en una lista lateral
 * el ojo escanea formas, no frases.
 *
 * El borde de color es la urgencia. Es lo único que grita, y grita una sola cosa.
 */
export function PanelSinMuelle({
  cargas, cargando, dark, seleccionada, onSeleccionar,
}: {
  cargas: CargaSalida[]
  cargando: boolean
  dark: boolean
  seleccionada?: string | null
  onSeleccionar?: (c: CargaSalida) => void
}) {
  const { t } = useTranslation()
  const [busqueda, setBusqueda] = useState('')
  const [orden, setOrden] = useState<Orden>('urgencia')
  const [soloUrgentes, setSoloUrgentes] = useState(false)

  const visibles = useMemo(() => {
    const term = busqueda.trim().toLowerCase()
    let lista = cargas
    if (term) {
      lista = lista.filter((c) =>
        c.carga.toLowerCase().includes(term)
        || (c.destino ?? '').toLowerCase().includes(term)
        || (c.cliente ?? '').toLowerCase().includes(term)
        || c.pedidos.some((p) => p.toLowerCase().includes(term)),
      )
    }
    if (soloUrgentes) lista = lista.filter((c) => enRiesgo(c) || retrasoDe(c) !== null)

    return [...lista].sort((a, b) => {
      if (orden === 'picking') return pickingPct(b) - pickingPct(a)
      if (orden === 'palets') return (b.palets ?? -1) - (a.palets ?? -1)
      // Urgencia: primero lo más atrasado; después, lo que antes sale.
      const ra = retrasoDe(a) ?? -1
      const rb = retrasoDe(b) ?? -1
      if (ra !== rb) return rb - ra
      return (a.fechaEnvioPrevista ?? '9999').localeCompare(b.fechaEnvioPrevista ?? '9999')
    })
  }, [cargas, busqueda, orden, soloUrgentes])

  const bdr = dark ? 'border-white/10' : 'border-slate-200'
  const campo = `w-full px-2 py-1 text-[11px] rounded border ${
    dark ? 'bg-slate-800 border-white/10 text-slate-100 placeholder:text-slate-500'
         : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'
  }`

  const tab = (activo: boolean) =>
    `px-1.5 py-0.5 text-[10px] font-semibold rounded transition-colors ${
      activo
        ? (dark ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-100 text-sky-700')
        : (dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700')
    }`

  return (
    <aside className={`rounded-xl border ${bdr} ${dark ? 'bg-slate-900/60' : 'bg-white'} flex flex-col overflow-hidden`}>
      <header className={`px-3 pt-3 pb-2 border-b ${bdr}`}>
        <div className="flex items-baseline justify-between gap-2">
          <h2 className={`text-[12px] font-bold ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
            {t('muelles.sinMuelle.titulo')}
          </h2>
          <span className={`text-[16px] font-black tabular-nums ${
            visibles.length > 0 ? 'text-rose-500' : (dark ? 'text-slate-600' : 'text-slate-300')
          }`}>
            {visibles.length}
          </span>
        </div>
        <p className={`text-[11px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
          {t('muelles.sinMuelle.ayuda')}
        </p>

        <div className="mt-2 space-y-1.5">
          <input
            className={campo}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder={t('muelles.sinMuelle.buscar')}
          />
          <div className="flex items-center gap-1">
            <button type="button" className={tab(orden === 'urgencia')} onClick={() => setOrden('urgencia')}>
              {t('muelles.sinMuelle.orden.urgencia')}
            </button>
            <button type="button" className={tab(orden === 'picking')} onClick={() => setOrden('picking')}>
              {t('muelles.sinMuelle.orden.picking')}
            </button>
            <button type="button" className={tab(orden === 'palets')} onClick={() => setOrden('palets')}>
              {t('muelles.sinMuelle.orden.palets')}
            </button>
            <button
              type="button"
              className={`ml-auto ${tab(soloUrgentes)}`}
              onClick={() => setSoloUrgentes((v) => !v)}
            >
              {t('muelles.sinMuelle.soloUrgentes')}
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto max-h-[62vh]">
        {cargando ? (
          <Loader label={t('muelles.sinMuelle.cargando')} />
        ) : visibles.length === 0 ? (
          <p className={`px-3 py-8 text-[11px] text-center ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
            {cargas.length === 0 ? t('muelles.sinMuelle.vacio') : t('muelles.sinMuelle.sinResultados')}
          </p>
        ) : (
          <ul>
            {visibles.map((c) => {
              const riesgo = enRiesgo(c)
              const retraso = retrasoDe(c)
              const activa = seleccionada === c.carga
              // El borde es la urgencia, y es lo único que grita en este panel.
              const urgencia = retraso !== null ? 'bg-rose-500'
                : riesgo ? 'bg-amber-500'
                : 'bg-transparent'
              return (
                <li key={`${c.empresa}-${c.carga}`} className={`border-b last:border-0 ${bdr}`}>
                  <button
                    type="button"
                    draggable
                    onDragStart={(e) => {
                      // Preparado para el arrastre: el carril ya sabe leer esto.
                      e.dataTransfer.setData('text/x-carga', c.carga)
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                    onClick={() => onSeleccionar?.(c)}
                    className={`relative w-full text-left pl-3 pr-3 py-2 transition-colors cursor-grab active:cursor-grabbing
                      ${activa
                        ? (dark ? 'bg-sky-950/60' : 'bg-sky-50')
                        : (dark ? 'hover:bg-white/5' : 'hover:bg-slate-50')}`}
                  >
                    <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${urgencia}`} aria-hidden />

                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-[12px] font-bold font-mono truncate ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
                        {c.carga}
                      </span>
                      <span className={`text-[10px] shrink-0 ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {c.almacen}
                      </span>
                    </div>

                    {/* Contraste subido a propósito: aquí estaba el gris flojo. */}
                    <div className={`text-[11px] truncate mb-1.5 ${dark ? 'text-slate-300' : 'text-slate-600'}`}>
                      {c.destino ?? c.cliente ?? c.pedidos[0] ?? '—'}
                    </div>

                    <div className="flex flex-wrap items-center gap-1">
                      <ChipFecha carga={c} dark={dark} />
                      <ChipPicking carga={c} dark={dark} />
                      <ChipPalets carga={c} dark={dark} />
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </aside>
  )
}
